// ── TrainingContent.jsx ───────────────────────────────────────────────────────
// FIXES:
//  - handleMouseMove throttled with requestAnimationFrame (was firing every mousemove — huge paint cost)
//  - tp-perks min column width reduced for tablet
//  - tp-steps responsive on 640px (single column)
//  - tp-track active state uses CSS variable instead of inline style override
import { useState, useRef, useCallback } from "react";
import { Eyebrow } from "./TrainingShared";
import { TRACKS, PERKS, STEPS } from "./data/trainingData";

export const CONTENT_CSS = `
  /* ── TRACKS ── */
  .tp-tracks {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 1px;
    margin-top: 3.5rem;
    background: var(--tp-line);
    border: 1px solid var(--tp-line);
    border-radius: 22px;
    overflow: hidden;
  }
  .tp-track {
    background: var(--tp-bg);
    padding: 1.875rem 1.5rem 1.75rem;
    cursor: pointer;
    transition: background .22s;
    position: relative;
    overflow: hidden;
  }
  .tp-track::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--tp-blue), var(--tp-violet));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform .28s cubic-bezier(.16,1,.3,1);
  }
  .tp-track:hover { background: var(--tp-surface2); }
  .tp-track:hover::before { transform: scaleX(1); }
  .tp-track.active { background: rgba(77,127,255,.055); }
  .tp-track.active::before { transform: scaleX(1); }
  .tp-track-emoji { font-size: 1.875rem; margin-bottom: .875rem; display: block; }
  .tp-track-name {
    font-family: var(--tp-display);
    font-size: .95rem; font-weight: 700;
    color: var(--tp-t1);
    margin-bottom: .375rem;
    letter-spacing: -.02em;
  }
  .tp-track-desc { font-size: .78rem; color: var(--tp-t3); line-height: 1.6; }
  .tp-track-arrow {
    position: absolute;
    right: 1rem; top: 50%;
    transform: translateY(-50%) translateX(-5px);
    color: var(--tp-blue);
    opacity: 0;
    transition: opacity .2s, transform .2s;
    font-size: .8rem;
  }
  .tp-track:hover .tp-track-arrow { opacity: 1; transform: translateY(-50%) translateX(0); }

  @media (max-width: 640px) {
    .tp-tracks { grid-template-columns: 1fr 1fr; }
    .tp-track  { padding: 1.25rem 1rem; }
    .tp-track-emoji { font-size: 1.5rem; }
  }
  @media (max-width: 480px) {
    .tp-tracks { grid-template-columns: 1fr; }
  }

  /* ── PERKS ── */
  .tp-perks {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
    margin-top: 3.5rem;
  }
  .tp-perk {
    background: var(--tp-surface);
    border: 1px solid var(--tp-line);
    border-radius: 22px;
    padding: 2rem;
    transition: border-color .22s, transform .22s cubic-bezier(.16,1,.3,1), box-shadow .22s;
    position: relative;
    overflow: hidden;
    will-change: transform;
  }
  .tp-perk::after {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(77,127,255,.055), transparent 58%);
    opacity: 0;
    transition: opacity .3s;
    pointer-events: none;
  }
  .tp-perk:hover { border-color: rgba(77,127,255,.24); transform: translateY(-4px); box-shadow: 0 24px 60px rgba(0,0,0,.32); }
  .tp-perk:hover::after { opacity: 1; }
  .tp-perk-emoji { font-size: 1.875rem; margin-bottom: 1.125rem; display: block; }
  .tp-perk-title {
    font-family: var(--tp-display);
    font-size: 1.05rem; font-weight: 700;
    color: var(--tp-t1);
    margin-bottom: .5rem;
    letter-spacing: -.02em;
  }
  .tp-perk-desc { font-size: .875rem; color: var(--tp-t2); line-height: 1.75; }

  @media (max-width: 640px) {
    .tp-perks { grid-template-columns: 1fr; }
    .tp-perk  { padding: 1.5rem; }
  }

  /* ── STEPS ── */
  .tp-steps {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    margin-top: 3.5rem;
    position: relative;
  }
  .tp-steps::before {
    content: '';
    position: absolute;
    top: 2.625rem;
    left: calc(12.5% + 2rem);
    right: calc(12.5% + 2rem);
    height: 1px;
    background: linear-gradient(90deg, var(--tp-blue), var(--tp-violet), var(--tp-pink));
    opacity: .2;
    z-index: 0;
  }
  .tp-step {
    padding: 0 2rem;
    text-align: center;
    position: relative;
    z-index: 1;
  }
  .tp-step-num-wrap {
    width: 52px; height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--tp-blue), var(--tp-violet));
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-size: .85rem;
    font-weight: 700;
    color: #fff;
    box-shadow: 0 4px 22px rgba(77,127,255,.38);
    position: relative;
  }
  .tp-step-num-wrap::before {
    content: '';
    position: absolute; inset: -5px;
    border-radius: 50%;
    border: 1px solid rgba(77,127,255,.22);
  }
  .tp-step-icon { font-size: 1.375rem; margin-bottom: .875rem; }
  .tp-step-title {
    font-family: var(--tp-display);
    font-size: 1rem; font-weight: 700;
    color: var(--tp-t1);
    margin-bottom: .5rem;
    letter-spacing: -.02em;
  }
  .tp-step-desc { font-size: .82rem; color: var(--tp-t2); line-height: 1.75; }

  @media (max-width: 900px) {
    .tp-steps { grid-template-columns: repeat(2, 1fr); gap: 2.5rem; }
    .tp-steps::before { display: none; }
    .tp-step { padding: 0 1rem; }
  }
  @media (max-width: 640px) {
    .tp-steps { grid-template-columns: 1fr; gap: 2rem; }
    .tp-step  { display: flex; align-items: flex-start; gap: 1.25rem; text-align: left; padding: 0; }
    .tp-step-num-wrap { margin: 0; flex-shrink: 0; }
  }
`;

/* ── Tracks ──────────────────────────────────────────────────────────────── */
export function TrainingTracks() {
  const [activeTrack, setActiveTrack] = useState(null);
  return (
    <section className="tp-section tp-section-alt">
      <div className="tp-container">
        <Eyebrow>Learning Tracks</Eyebrow>
        <h2 className="tp-section-h2">
          Pick Your <em>Field</em>
        </h2>
        <p className="tp-section-sub">
          Eight specialisations. Real work. One cohort fee.
        </p>
        <div className="tp-tracks">
          {TRACKS.map((t) => (
            <div
              key={t.name}
              className={`tp-track${activeTrack === t.name ? " active" : ""}`}
              onClick={() => setActiveTrack(activeTrack === t.name ? null : t.name)}
            >
              <span className="tp-track-emoji">{t.icon}</span>
              <div className="tp-track-name">{t.name}</div>
              <div className="tp-track-desc">{t.desc}</div>
              <span className="tp-track-arrow">→</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Perks ───────────────────────────────────────────────────────────────── */
export function TrainingPerks() {
  // Throttle mousemove via RAF — prevents layout thrash on rapid mouse movement
  const rafRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      
      // Add null checks to prevent errors
      if (!e?.currentTarget || typeof e.currentTarget.getBoundingClientRect !== 'function') {
        return;
      }
      
      try {
        const rect = e.currentTarget.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0) {
          return;
        }
        
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        
        if (e.currentTarget.style) {
          e.currentTarget.style.setProperty("--mx", `${x}%`);
          e.currentTarget.style.setProperty("--my", `${y}%`);
        }
      } catch (error) {
        console.warn('Error in mouse move handler:', error);
      }
    });
  }, []);

  return (
    <section className="tp-section">
      <div className="tp-container">
        <Eyebrow>Why QuantumLogics</Eyebrow>
        <h2 className="tp-section-h2">
          What You <em>Get</em>
        </h2>
        <p className="tp-section-sub">
          Beyond skills — real experience, real connections, real credentials.
          Plus access to Pakistan's largest developer community.
        </p>
        <div className="tp-perks">
          {PERKS.map((p) => (
            <div key={p.title} className="tp-perk" onMouseMove={handleMouseMove}>
              <span className="tp-perk-emoji">{p.icon}</span>
              <div className="tp-perk-title">{p.title}</div>
              <div className="tp-perk-desc">{p.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Steps ───────────────────────────────────────────────────────────────── */
export function TrainingSteps() {
  return (
    <section className="tp-section tp-section-alt">
      <div className="tp-container">
        <Eyebrow>How It Works</Eyebrow>
        <h2 className="tp-section-h2">
          Four <em>Steps</em>
        </h2>
        <p className="tp-section-sub">
          From enrollment to internship completion — the full journey.
        </p>
        <div className="tp-steps">
          {STEPS.map((s) => (
            <div key={s.num} className="tp-step">
              <div className="tp-step-num-wrap">{s.num}</div>
              <div>
                <div className="tp-step-icon">{s.icon}</div>
                <div className="tp-step-title">{s.title}</div>
                <div className="tp-step-desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
